// FIX 3 (editor thumbnails / faster loading): downscale an image Blob to a
// ~1600px long side JPEG so the editor can render a sharp-but-lighter tier
// instead of pulling the full-resolution original for every slot. (512px was
// too soft on the editor canvas; 1600px stays sharp while loading faster than
// the originals.) The full-res original is uploaded unchanged for PDF — this is
// purely an additional, smaller sibling.
//
// Best-effort by contract: ALWAYS resolves (never rejects). Returns null when
// the canvas pipeline is unavailable or decode fails so the upload flow can
// simply fall back to the preview/original. Callers should treat null as
// "no thumbnail, carry on".
//
// Prefers createImageBitmap + OffscreenCanvas (works on the main thread and in
// workers, decodes off the UI thread). Falls back to a DOM <img> + <canvas>
// when OffscreenCanvas is missing but document is available (older Safari).

function scaledDims(w: number, h: number, maxDim: number): { w: number; h: number } {
  const longest = Math.max(w, h);
  if (longest <= maxDim) return { w, h };
  const scale = maxDim / longest;
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}

// Path A: createImageBitmap + OffscreenCanvas (preferred — off-thread decode).
async function viaOffscreen(
  file: Blob,
  maxDim: number,
  quality: number,
): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file);
  try {
    const { w, h } = scaledDims(bitmap.width, bitmap.height, maxDim);
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, w, h);
    return await canvas.convertToBlob({ type: 'image/jpeg', quality });
  } finally {
    bitmap.close?.();
  }
}

// Path B: DOM <img> + <canvas> (fallback for browsers without OffscreenCanvas).
function viaDomCanvas(
  file: Blob,
  maxDim: number,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { w, h } = scaledDims(img.naturalWidth, img.naturalHeight, maxDim);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export async function makeThumbnail(
  file: Blob,
  maxDim = 1600,
  quality = 0.82,
): Promise<Blob | null> {
  try {
    if (
      typeof createImageBitmap === 'function' &&
      typeof OffscreenCanvas !== 'undefined'
    ) {
      return await viaOffscreen(file, maxDim, quality);
    }
    if (typeof document !== 'undefined' && typeof URL?.createObjectURL === 'function') {
      return await viaDomCanvas(file, maxDim, quality);
    }
    return null; // no canvas pipeline (e.g. node) — caller falls back
  } catch {
    return null; // decode / encode failure — never break the upload flow
  }
}
